import { CALL_SLOT_STATE } from "./callStates.js";
import { CALL_DIRECTION, CALL_ROLE, } from "./callDirections.js";
import { RESUME_STATE, SIGNALING_CONNECTION_STATE, } from "./signalingTypes.js";
import { makeInitialLocalMediaState, } from "./mediaTypes.js";
import { makeInitialLocalRtcState, } from "./rtcTypes.js";
/**
 * makeInitialCallModel
 * --------------------
 *
 * Factory for the initial root model.
 */
export function makeInitialCallModel() {
    return {
        signalingConnectionState: SIGNALING_CONNECTION_STATE.DISCONNECTED,
        resumeState: RESUME_STATE.NONE,
        session: null,
        callSlotState: CALL_SLOT_STATE.IDLE,
        activeCall: null,
        media: makeInitialLocalMediaState(),
        rtc: makeInitialLocalRtcState(),
        error: null,
    };
}
/**
 * makeActiveCallContext
 * ---------------------
 *
 * Convenience factory for constructing an active call context from
 * authoritative data already known by the client.
 */
export function makeActiveCallContext(args) {
    return {
        callId: args.callId,
        serverCallState: args.serverCallState,
        direction: args.direction,
        selfRole: args.selfRole,
        callerUserId: args.callerUserId,
        calleeUserId: args.calleeUserId,
        remoteUserId: args.remoteUserId,
        media: args.media,
        features: args.features ?? {},
        participants: args.participants ?? {},
        updatedAt: args.updatedAt,
        ringingExpiresAt: args.ringingExpiresAt ?? null,
        endedReason: args.endedReason ?? null,
        endedBy: args.endedBy ?? null,
    };
}
/**
 * makeUnknownActiveCallContext
 * ----------------------------
 *
 * Convenience factory for cases where the client establishes an active
 * call context before receiving a full authoritative CALL_STATE snapshot.
 *
 * This is useful for flows like:
 * - CALL_CREATED received by caller
 * - CALL_INCOMING received by callee
 *
 * where the client already has enough information to enter ACTIVE call-slot
 * state, but not yet the full server snapshot.
 */
export function makeUnknownActiveCallContext(args) {
    return makeActiveCallContext({
        callId: args.callId,
        serverCallState: args.serverCallState,
        direction: args.direction,
        selfRole: args.selfRole,
        callerUserId: args.callerUserId,
        calleeUserId: args.calleeUserId,
        remoteUserId: args.remoteUserId,
        media: args.media,
        features: {},
        participants: {},
        updatedAt: args.updatedAt,
        ringingExpiresAt: args.ringingExpiresAt ?? null,
        endedReason: null,
        endedBy: null,
    });
}
/**
 * deriveRemoteUserId
 * ------------------
 *
 * Derives the remote participant userId from authoritative caller/callee ids
 * and the local user's userId.
 */
export function deriveRemoteUserId(args) {
    if (!args.localUserId)
        return null;
    if (args.localUserId === args.callerUserId)
        return args.calleeUserId;
    if (args.localUserId === args.calleeUserId)
        return args.callerUserId;
    return null;
}
/**
 * deriveDirectionAndRole
 * ----------------------
 *
 * Derives client-perspective direction and role from authoritative
 * caller/callee ids and the local user's userId.
 */
export function deriveDirectionAndRole(args) {
    if (!args.localUserId) {
        return {
            direction: CALL_DIRECTION.NONE,
            selfRole: CALL_ROLE.NONE,
        };
    }
    if (args.localUserId === args.callerUserId) {
        return {
            direction: CALL_DIRECTION.OUTGOING,
            selfRole: CALL_ROLE.CALLER,
        };
    }
    if (args.localUserId === args.calleeUserId) {
        return {
            direction: CALL_DIRECTION.INCOMING,
            selfRole: CALL_ROLE.CALLEE,
        };
    }
    return {
        direction: CALL_DIRECTION.NONE,
        selfRole: CALL_ROLE.NONE,
    };
}
